import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  X,
  Clock,
  MapPin,
  Users,
  Calendar,
  Share2,
  Bell,
  BellRing,
  Play,
  User,
  Radio,
} from 'lucide-react';

import { useAppStore } from '../../stores/appStore';
import { useReminder } from '../../hooks/useEvents';
import { useEventInteraction } from '../../hooks/useEvents';

const EventModal = () => {
  const { t } = useTranslation();
  const { currentEvent, setCurrentEvent, getEventProgress, isEventActive } = useAppStore();
  const { toggleReminder, isReminderSet } = useReminder();
  const { trackInteraction } = useEventInteraction();

  if (!currentEvent) return null;

  const isActive = isEventActive(currentEvent);
  const progress = getEventProgress(currentEvent);
  const hasReminder = isReminderSet(currentEvent.id);
  
  const startTime = new Date(currentEvent.startTime);
  const endTime = new Date(currentEvent.endTime);

  const handleClose = () => {
    setCurrentEvent(null);
  };

  const handleReminderClick = () => {
    toggleReminder(currentEvent.id);
    trackInteraction(currentEvent.id, 'remind');
  };

  const handleShareClick = () => {
    trackInteraction(currentEvent.id, 'share');
    
    if (navigator.share) {
      navigator.share({
        title: currentEvent.title,
        text: currentEvent.description,
        url: window.location.origin + `/events/${currentEvent.id}`,
      });
    } else {
      navigator.clipboard.writeText(`${currentEvent.title} - ${window.location.origin}/events/${currentEvent.id}`);
    }
  };

  const categoryColors = {
    KARAOKE: 'from-green-500 to-green-600',
    GAMING: 'from-pink-500 to-pink-600',
    WORKSHOP: 'from-teal-500 to-teal-600',
    EXHIBITION: 'from-orange-500 to-orange-600',
    CONFERENCE: 'from-blue-500 to-blue-600',
    NETWORKING: 'from-purple-500 to-purple-600',
    SOCIAL: 'from-pink-500 to-rose-600',
    OTHER: 'from-gray-500 to-gray-600',
  };

  const categoryGradient = categoryColors[currentEvent.category] || 'from-gray-500 to-gray-600';

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleClose}
    >
      <motion.div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl"
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Image */}
        <div className={`relative h-48 bg-gradient-to-br ${categoryGradient}`}>
          {currentEvent.imageUrl && (
            <img
              src={currentEvent.imageUrl}
              alt={currentEvent.title}
              className="w-full h-full object-cover"
            />
          )}
          
          {/* Gradient Overlay */}
          <div className={`absolute inset-0 bg-gradient-to-t ${categoryGradient} opacity-80`} />
          
          {/* Live Badge */}
          {currentEvent.isStreaming && isActive && (
            <motion.div
              className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center space-x-2"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Radio className="w-4 h-4" />
              <span>{t('events.streaming.live')}</span>
            </motion.div>
          )}

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors backdrop-blur-sm"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Category Badge */}
          <div className="absolute bottom-4 left-4">
            <span className="bg-white bg-opacity-20 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full font-medium">
              {currentEvent.category}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-12rem)]">
          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {currentEvent.title}
          </h2>

          {/* Event Details */}
          <div className="space-y-4 mb-6">
            {/* Time */}
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Clock className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <div className="font-medium text-gray-800">
                  {format(startTime, 'EEEE, d MMMM', { locale: es })}
                </div>
                <div className="text-gray-600 text-sm">
                  {format(startTime, 'HH:mm')} - {format(endTime, 'HH:mm')}
                </div>
              </div>
            </div>

            {/* Location */}
            {currentEvent.location && (
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <MapPin className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-800">Ubicación</div>
                  <div className="text-gray-600 text-sm">{currentEvent.location}</div>
                </div>
              </div>
            )}

            {/* Capacity */}
            {currentEvent.capacity && (
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Users className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-800">Capacidad</div>
                  <div className="text-gray-600 text-sm">
                    {currentEvent.currentAttendees || 0} / {currentEvent.capacity} personas
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {isActive && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-green-600">
                  {t('events.status.active')}
                </span>
                <span className="text-sm text-gray-500">
                  {Math.round(progress)}% completado
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <motion.div
                  className="bg-green-500 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                />
              </div>
            </div>
          )}

          {/* Description */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">Descripción</h3>
            <p className="text-gray-600 leading-relaxed">
              {currentEvent.description}
            </p>
          </div>

          {/* Speakers */}
          {currentEvent.speakers && currentEvent.speakers.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Ponentes</h3>
              <div className="space-y-3">
                {currentEvent.speakers.map((speaker) => (
                  <div key={speaker.id} className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center">
                      {speaker.avatarUrl ? (
                        <img
                          src={speaker.avatarUrl}
                          alt={speaker.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">{speaker.name}</div>
                      <div className="text-sm text-gray-600">{speaker.title}</div>
                      {speaker.company && (
                        <div className="text-xs text-gray-500">{speaker.company}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {/* Streaming Button */}
            {currentEvent.isStreaming && isActive && (
              <button
                className="w-full bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors"
                onClick={() => {
                  if (currentEvent.streamUrl) {
                    window.open(currentEvent.streamUrl, '_blank');
                  }
                }}
              >
                <Play className="w-5 h-5" />
                <span>{t('events.streaming.watchLive')}</span>
              </button>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleReminderClick}
                className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${
                  hasReminder
                    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {hasReminder ? (
                  <BellRing className="w-5 h-5" />
                ) : (
                  <Bell className="w-5 h-5" />
                )}
                <span>
                  {hasReminder 
                    ? t('events.actions.removeReminder')
                    : t('events.actions.addReminder')
                  }
                </span>
              </button>

              <button
                onClick={handleShareClick}
                className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                <span>{t('events.actions.share')}</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EventModal;